<template>
  <div class="image-display-wrapper">
    <input
      ref="inputImagen"
      type="file"
      :accept="acceptTypes"
      class="hidden-input"
      @change="onFileSelected"
    />

    <!-- Estado: con imagen -->
    <div
      v-if="displayUrl"
      class="image-container"
      :class="[{ 'is-editable': editable }, variantClass]"
    >
      <img :src="displayUrl" :alt="imageName" class="display-image" />

      <div v-if="editable" class="image-overlay">
        <Button
          rounded
          class="overlay-btn edit-btn"
          type="button"
          v-tooltip.top="'Reemplazar imagen'"
          @click="triggerUpload"
        >
          <i class="pi pi-pencil"></i>
        </Button>

        <Button
          rounded
          severity="danger"
          class="overlay-btn delete-btn"
          type="button"
          v-tooltip.top="'Eliminar imagen'"
          @click="confirmarEliminacion"
        >
          <i class="pi pi-trash"></i>
        </Button>
      </div>
    </div>

    <!-- Estado: sin imagen -->
    <button
      v-else
      type="button"
      class="empty-container"
      :class="[{ 'is-editable': editable }, variantClass]"
      :disabled="!editable"
      @click="onEmptyClick"
    >
      <div class="empty-icon">
        <i :class="['pi', emptyIcon]"></i>
      </div>
      <p class="empty-text">{{ emptyText }}</p>
      <p v-if="editable" class="empty-hint">{{ emptyHint }}</p>
    </button>
  </div>
</template>

<script>
import Button from "primevue/button";

export default {
  name: "ZapiImageManager",
  components: {
    Button,
  },
  props: {
    /** URL de la imagen desde la BD (relativa o absoluta) */
    currentImage: {
      type: String,
      default: "",
    },
    /** Nombre/alt de la imagen */
    imageName: {
      type: String,
      default: "Imagen",
    },
    /** Texto cuando no hay imagen */
    emptyText: {
      type: String,
      default: "Sin imagen",
    },
    /** Subtexto cuando no hay imagen y es editable */
    emptyHint: {
      type: String,
      default: "Click para subir imagen",
    },
    /** Icono PrimeIcons del estado vacío, sin el prefijo "pi-" */
    emptyIcon: {
      type: String,
      default: "pi-image",
    },
    /** Si se permiten acciones de editar/eliminar */
    editable: {
      type: Boolean,
      default: true,
    },
    /** Tipos MIME aceptados al reemplazar */
    acceptTypes: {
      type: String,
      default: "image/png,image/jpeg,image/jpg,image/webp",
    },
    /** Tamaño máximo en MB */
    maxSizeMB: {
      type: Number,
      default: 5,
    },
    /** 'profile' | 'logo' | '' */
    variant: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["upload", "remove"],
  data() {
    return {
      previewUrl: "",
      isDeleted: false,
    };
  },
  computed: {
    variantClass() {
      return this.variant ? `variant-${this.variant}` : "";
    },
    displayUrl() {
      if (this.isDeleted) return "";
      return this.previewUrl || this.normalizeImageUrl(this.currentImage);
    },
  },
  watch: {
    currentImage() {
      // Si cambia la URL desde afuera (ej. al abrir otro registro), reiniciamos estados locales.
      this.previewUrl = "";
      this.isDeleted = false;
    },
  },
  methods: {
    normalizeImageUrl(image) {
      if (!image) return "";
      if (image.startsWith("http://") || image.startsWith("https://")) {
        return image;
      }
      if (image.startsWith("data:image")) {
        return image;
      }
      return `${process.env.VUE_APP_ROOT_API.replace(/\/api\/?$/, "")}/storage/${image}`;
    },
    triggerUpload(event) {
      event?.stopPropagation();
      this.$refs.inputImagen?.click();
    },
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      this.isDeleted = false;

      if (!file.type.startsWith("image/")) {
        console.warn("Solo se permiten imágenes");
        return;
      }

      if (file.size > this.maxSizeMB * 1024 * 1024) {
        console.warn(`El archivo no puede exceder ${this.maxSizeMB}MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);

      this.$emit("upload", file);
      event.target.value = "";
    },
    confirmarEliminacion(event) {
      event?.stopPropagation();
      this.$confirm.require({
        header: "Confirmar eliminación",
        message: "¿Deseas quitar esta imagen?",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sí, quitar",
        rejectLabel: "Cancelar",
        acceptClass: "p-button-danger",
        accept: () => {
          this.previewUrl = "";
          this.isDeleted = true;
          this.$emit("remove");
        },
      });
    },
    onEmptyClick() {
      if (this.editable) {
        this.$refs.inputImagen?.click();
      }
    },
  },
};
</script>

<style scoped>
.image-display-wrapper {
  --accent: #534ab7;
  --accent-gradient: linear-gradient(135deg, #534ab7 0%, #7b6fe0 100%);
  --accent-light: rgba(83, 74, 183, 0.06);
  --accent-border: rgba(83, 74, 183, 0.15);
  --danger: #ff4d4f;
  --radius: 16px;
  --transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 8px 24px rgba(83, 74, 183, 0.12);

  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hidden-input {
  display: none;
}

/* ─── Contenedor con imagen ─────────────────────────── */
.image-container {
  position: relative;
  width: 100%;
  max-width: 320px;
  border-radius: var(--radius);
  overflow: hidden;
  border: 2px solid var(--accent-border);
  background: #fff;
  transition: all var(--transition);
  box-shadow: var(--shadow-sm);
}

.image-container.is-editable {
  cursor: pointer;
}

.image-container.is-editable:hover {
  border-color: var(--accent);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.image-container.is-editable:hover .image-overlay {
  opacity: 1;
}

.display-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #fdfdff;
  transition: transform var(--transition);
}

.image-container:hover .display-image {
  transform: scale(1.03);
}

/* ─── Overlay de acciones ────────────────────────────── */
.image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity var(--transition);
  border-radius: var(--radius);
}

.overlay-btn {
  width: 48px !important;
  height: 48px !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border: none !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: all var(--transition) !important;
}

.overlay-btn:hover {
  transform: scale(1.15) !important;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.overlay-btn.edit-btn {
  background: var(--accent-gradient) !important;
  color: #fff !important;
}

.overlay-btn.delete-btn {
  background: #fff !important;
  color: var(--danger) !important;
}

/* ─── Estado vacío ───────────────────────────────────── */
.empty-container {
  width: 100%;
  max-width: 320px;
  aspect-ratio: 1 / 1;
  border: 2px dashed var(--accent-border);
  border-radius: var(--radius);
  background: #fdfdff;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all var(--transition);
  position: relative;
  outline: none;
  cursor: default;
}

.empty-container.is-editable {
  cursor: pointer;
}

.empty-container.is-editable:hover {
  border-color: var(--accent);
  background: var(--accent-light);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.empty-container.is-editable:hover .empty-icon {
  background: var(--accent-gradient);
  box-shadow: 0 8px 16px rgba(83, 74, 183, 0.3);
}

.empty-container.is-editable:hover .empty-icon i {
  color: #fff;
  transform: rotate(15deg);
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(83, 74, 183, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  transition: all var(--transition);
}

.empty-icon i {
  font-size: 28px;
  color: var(--accent);
  transition: all var(--transition);
}

.empty-text {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  letter-spacing: -0.01em;
}

.empty-hint {
  font-size: 13px;
  color: #6e6e80;
  margin: 0;
  max-width: 200px;
  line-height: 1.4;
}

/* ─── Variantes ──────────────────────────────────────── */
.image-container.variant-profile,
.empty-container.variant-profile {
  aspect-ratio: 1 / 1;
  max-width: 320px;
}

.image-container.variant-profile .display-image {
  object-fit: cover;
}

.image-container.variant-logo,
.empty-container.variant-logo {
  aspect-ratio: auto;
  max-width: 350px;
  width: 100%;
  padding: 16px;
  background: transparent;
  box-shadow: none;
  border-color: transparent;
}

.image-container.variant-logo {
  border: 1px solid var(--accent-border);
  background: #fff;
  border-radius: 12px;
}

.empty-container.variant-logo {
  border: 2px dashed var(--accent-border);
  aspect-ratio: 16 / 9;
}

.image-container.variant-logo .display-image {
  object-fit: contain;
  background: transparent;
  max-height: 200px;
}
</style>
